import { ElementType, InputHTMLAttributes } from 'react'

import Typography from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
      theme?: 'dark' | 'light'
    }
  >

const FormControl = ({
  id,
  type,
  label,
  checked,
  // eslint-disable-next-line no-unused-vars
  options,
  placeholder,
  className,
  children,
  theme = 'light',
  error,
  ...props
}: FormControlProps) => {
  const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

  return (
    <div
      className={cn(className, {
        hidden: type === 'hidden'
      })}
    >
      {type === 'consent' ? (
        <label
          // htmlFor={id}
          className="relative flex items-start space-x-[1rem]"
        >
          <input
            id={id}
            type="checkbox"
            checked={checked}
            className={cn([
              'h-[1.5rem] w-[1.5rem] opacity-0',
              {
                '[&:checked+div]:after:bg-white/80': theme === 'light',
                '[&:checked+div]:after:bg-dark-blue/80': theme === 'dark'
              }
            ])}
            {...props}
          />
          <div
            className={cn([
              'absolute -top-0 left-0 !m-0 h-[1.5rem] w-[1.5rem] shrink-0 cursor-pointer rounded-full border-2',
              'after:absolute after:left-1/2 after:top-1/2 after:h-[.9rem] after:w-[.9rem] after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full',
              {
                'border-white/20': theme === 'light',
                'border-dark-blue/20': theme === 'dark',
                'border-error': error
              }
            ])}
          />
          <Typography
            variant="small"
            className={cn([
              '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
              {
                'text-white': theme === 'light',
                'text-dark-blue': theme === 'dark',
                '!text-error': error
              }
            ])}
          >
            {label}
          </Typography>
        </label>
      ) : (
        <Component
          id={id}
          type={type}
          placeholder={placeholder}
          className={cn([
            'flex w-full appearance-none rounded-[3rem] bg-transparent px-[3rem] py-[1.5rem] font-garet text-body outline-none',
            'border',
            'focus:ring-2',
            {
              'border-white/20 text-white placeholder:text-white':
                theme === 'light',
              'border-dark-blue/20 text-dark-blue placeholder:text-dark-blue':
                theme === 'dark'
            },
            {
              'focus:border-white/80 focus:ring-white/40':
                !error && theme === 'light',
              'focus:border-dark-blue/80 focus:ring-dark-blue/40':
                !error && theme === 'dark',
              'border-error focus:ring-error/50': error
            }
          ])}
          {...props}
        />
      )}
      {children}
    </div>
  )
}

export default FormControl
